// ============================================================
// products.nav.ts — Phase-3 migration descriptor
// ------------------------------------------------------------
// Component-free, data-only navigation descriptor for the organization
// Products feature (parent + All products / Product blueprints / Routine
// blueprints children). Imports ONLY pure types/constants
// (@twist/ui-navigation types + @twist/core security primitives) — no
// component / feature code.
//
// Security mirrors the STRICTER of the legacy chrome: the legacy sidebar
// "Products" group required requireOrganization + MyOrganization__Products_Read,
// and each sub-item carried its own stricter read perm. We keep those
// stricter NAV constraints so nav-visible ⊆ route-accessible (route guards
// were weaker — All products + Routine blueprints had none).
// ============================================================
import { OrgPermissions } from '@twist/core';
import type { NavDescriptor } from '@twist/ui-navigation';

export const productsNav: NavDescriptor = {
  id: 'products',
  path: '/products',
  label: 'Products',
  icon: 'qrCode',
  group: 'Organization',
  // Organization section = 20s; Products is first (matches the recovered
  // sideNavConfig order: Products, Devices, Metadata, Security).
  order: 20,
  surfaces: ['sidebar', 'palette'],
  security: {
    requireOrganization: true,
    orgPermissions: [OrgPermissions.MyOrganization__Products_Read],
  },
  children: [
    {
      id: 'products-all',
      // Moved from `/products` → `/products/search` (the list/search page is
      // now a `search` child; `/products` redirects here). The sidebar
      // sub-item + ⌘K entry + breadcrumb all resolve against this path.
      path: '/products/search',
      label: 'All products',
      group: 'Organization',
      surfaces: ['sidebar', 'palette'],
      security: {
        requireOrganization: true,
        orgPermissions: [OrgPermissions.MyOrganization__Products_Read],
      },
      // Entity-detail tabs for /products/:productKey (F19 Wave 2 — the four
      // routed tabs). The detail route carries navId 'products-all', so the
      // breadcrumb walker + routed `tw-page-header-tabs [navId]` strip
      // synthesise these as the Dashboard · Configuration · Metadata ·
      // Version history sibling tabs (mirrors how installations.nav.ts hangs
      // tab children off the 'installations' descriptor). All gate the same
      // Products_Read perm as the parent — no per-tab divergence. Product
      // Blueprints get the SAME set minus Dashboard (see productBlueprintTabs).
      // F19 Wave 5 / Wave 8 — per-tab icons. Wave 5 used `lucide:*` IconStore
      // refs resolved by `tw-icon`, which rendered BLANK in the real app (those
      // refs aren't prefetched and 404 on demand). Wave 8 switched the tab strip
      // to the STATIC `ui-svg-icon` sprite (`SvgIcons` enum) — the same path the
      // sidebar nav uses — so `icon` now holds a `SvgIcons` key. Mapping keeps
      // the Wave 5 intent: Dashboard≈grid→`dashboard`, Configuration≈sliders→
      // `gears`, Metadata≈tags→`tag`, Version history≈history→`timeLapse`.
      children: [
        {
          // NOTE: descriptor `id` kept as `product-analytics` (stable
          // active-trail / badge key, not user-visible). Only the URL `path`
          // segment renamed `analytics` → `dashboard` (Item 2) to match the
          // design name; the `label` ('Dashboard') was already correct.
          id: 'product-analytics',
          path: 'dashboard',
          label: 'Dashboard',
          icon: 'dashboard',
          surfaces: ['tabs', 'breadcrumb'],
          order: 1,
          security: {
            requireOrganization: true,
            orgPermissions: [OrgPermissions.MyOrganization__Products_Read],
          },
        },
        {
          id: 'product-configuration',
          path: 'configuration',
          label: 'Configuration',
          icon: 'gears',
          surfaces: ['tabs', 'breadcrumb'],
          order: 2,
          security: {
            requireOrganization: true,
            orgPermissions: [OrgPermissions.MyOrganization__Products_Read],
          },
        },
        {
          id: 'product-metadata',
          path: 'metadata',
          label: 'Metadata',
          icon: 'tag',
          surfaces: ['tabs', 'breadcrumb'],
          order: 3,
          security: {
            requireOrganization: true,
            orgPermissions: [OrgPermissions.MyOrganization__Products_Read],
          },
        },
        {
          id: 'product-version-history',
          // Locked slug `/history` (NOT /versions). The count pill
          // (versions.length) is supplied reactively by a NAV_BADGE_PROVIDER
          // — see ProductVersionsTabBadgeProvider — keyed on this id.
          path: 'history',
          label: 'Version history',
          icon: 'timeLapse',
          surfaces: ['tabs', 'breadcrumb'],
          order: 4,
          security: {
            requireOrganization: true,
            orgPermissions: [OrgPermissions.MyOrganization__Products_Read],
          },
        },
      ],
    },
    {
      id: 'product-blueprints',
      // Moved from the old top-level `/product-blueprints` → nested
      // `/products/blueprints` (no redirect from the old path — it 404s).
      path: '/products/blueprints',
      label: 'Product blueprints',
      group: 'Organization',
      surfaces: ['sidebar', 'palette'],
      security: {
        requireOrganization: true,
        orgPermissions: [OrgPermissions.MyOrganization__ProductBlueprints_Read],
      },
      // F19 Wave 2 — Product Blueprints get the SAME entity-detail tab set as
      // Products MINUS Dashboard (PB has no snapshot), defaulting to
      // Configuration. F19 Wave 4c — PLUS a deliberate PB-only 4th tab,
      // **Settings** (version-agnostic blueprint description; placed last). So
      // PB = Configuration · Metadata · Version history · Settings.
      // F19 Wave 5 / Wave 8 — per-tab icons, now STATIC `SvgIcons` keys (see the
      // Products tab note above; Wave 8 fixed the blank `tw-icon`/lucide path).
      // Mapping: Configuration≈sliders→`gears`, Metadata≈tags→`tag`, Version
      // history≈history→`timeLapse`, Settings≈settings/gear→`gearsDatabase`
      // (a distinct gear glyph so Settings reads apart from Configuration).
      children: [
        {
          id: 'product-blueprint-configuration',
          path: 'configuration',
          label: 'Configuration',
          icon: 'gears',
          surfaces: ['tabs', 'breadcrumb'],
          order: 1,
          security: {
            requireOrganization: true,
            orgPermissions: [OrgPermissions.MyOrganization__ProductBlueprints_Read],
          },
        },
        {
          id: 'product-blueprint-metadata',
          path: 'metadata',
          label: 'Metadata',
          icon: 'tag',
          surfaces: ['tabs', 'breadcrumb'],
          order: 2,
          security: {
            requireOrganization: true,
            orgPermissions: [OrgPermissions.MyOrganization__ProductBlueprints_Read],
          },
        },
        {
          id: 'product-blueprint-version-history',
          path: 'history',
          label: 'Version history',
          icon: 'timeLapse',
          surfaces: ['tabs', 'breadcrumb'],
          order: 3,
          security: {
            requireOrganization: true,
            orgPermissions: [OrgPermissions.MyOrganization__ProductBlueprints_Read],
          },
        },
        {
          // F19 Wave 4c — slim Settings tab (version-AGNOSTIC: blueprint
          // description). Placed LAST; contributes no version chrome.
          id: 'product-blueprint-settings',
          path: 'settings',
          label: 'Settings',
          icon: 'gearsDatabase',
          surfaces: ['tabs', 'breadcrumb'],
          order: 4,
          security: {
            requireOrganization: true,
            orgPermissions: [OrgPermissions.MyOrganization__ProductBlueprints_Read],
          },
        },
      ],
    },
    {
      id: 'routine-blueprints',
      path: '/products/routine-blueprints',
      label: 'Routine blueprints',
      group: 'Organization',
      surfaces: ['sidebar', 'palette'],
      security: {
        requireOrganization: true,
        orgPermissions: [OrgPermissions.MyOrganization__RoutineBlueprints_Read],
      },
    },
  ],
};
